import { api } from '../api';
import { isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatCurrency, formatDate, statusBadge, timeAgo, icons } from '../helpers';
import { toast, openModal, closeModal, confirmDialog, setBreadcrumb, skeletonCards, bindSearch } from '../ui';

export async function renderProjects() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Projects' }]);
  setHTML(content, skeletonCards(4));

  try {
    const data = await api.get('/projects');
    const projects = data.projects || [];
    let clientsOpts = '';
    if (isAdmin()) {
      const cd = await api.get('/clients');
      clientsOpts = (cd.clients || []).map((c: any) => `<option value="${c.id}">${esc(c.name)}</option>`).join('');
    }
    (window as any)._clientsOpts = clientsOpts;

    let cardsHtml = '';
    if (!projects.length) {
      cardsHtml = '<div class="empty-state"><i data-lucide="folder-kanban"></i><p>No projects yet</p></div>';
    } else {
      projects.forEach((p: any) => {
        cardsHtml += `<div class="card project-card" data-status="${esc(p.status)}" style="margin-bottom:0.8rem">
          <a href="/projects/${p.id}" style="text-decoration:none;color:inherit;display:block" class="card-body">
          <div class="flex items-center justify-between mb-1"><h3 style="font-family:var(--font-heading);font-weight:600;font-size:1.05rem">${esc(p.title)}</h3>${statusBadge(p.status)}</div>
          ${isAdmin() ? `<div class="text-muted mb-1" style="font-size:0.85rem">${esc(p.client_name || '')}${p.client_company ? ' \u2014 ' + esc(p.client_company) : ''}</div>` : ''}
          ${p.description ? `<p class="text-muted" style="font-size:0.88rem;margin-bottom:0.8rem">${esc(p.description.substring(0, 120))}${p.description.length > 120 ? '...' : ''}</p>` : ''}
          <div class="flex items-center justify-between">
            <div class="flex items-center" style="gap:1.5rem">
              ${p.budget ? `<span class="text-muted" style="font-size:0.85rem"><strong style="color:var(--text-primary)">${formatCurrency(p.budget, p.currency)}</strong></span>` : ''}
              ${p.deadline ? `<span class="text-muted" style="font-size:0.85rem">Due ${formatDate(p.deadline)}</span>` : ''}
            </div>
            <div style="width:120px"><div class="progress-bar"><div class="progress-fill" style="width:${esc(p.progress || 0)}%"></div></div><div class="progress-text">${esc(p.progress || 0)}%</div></div>
          </div></a></div>`;
      });
    }

    setHTML(content, `<div class="page-enter">
      <div class="page-header"><h1>Projects</h1>
      ${isAdmin() ? '<div class="page-header-actions"><div class="search-input-wrapper"><i data-lucide="search"></i><input type="text" id="projects-search" placeholder="Search..."></div><button class="btn btn-primary" id="add-project-btn"><i data-lucide="plus"></i> New Project</button></div>' : ''}
      </div>
      <div class="filters-bar">
        <button class="filter-chip active" data-filter="all">All</button>
        <button class="filter-chip" data-filter="pending">Pending</button>
        <button class="filter-chip" data-filter="in_progress">In Progress</button>
        <button class="filter-chip" data-filter="review">Review</button>
        <button class="filter-chip" data-filter="completed">Completed</button>
      </div>
      <div id="projects-list">${cardsHtml}</div>
    </div>`);

    icons();
    $$('.filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        $$('.filter-chip').forEach(c => c.classList.remove('active'));
        chip.classList.add('active');
        const filter = chip.getAttribute('data-filter')!;
        $$('.project-card').forEach((card: any) => {
          card.style.display = (filter === 'all' || card.dataset.status === filter) ? '' : 'none';
        });
      });
    });
    bindSearch('projects-search', '.project-card', '#projects-list');
    document.getElementById('add-project-btn')?.addEventListener('click', () => openProjectForm());
  } catch (err: any) { toast(err.message, 'error'); }
}

export async function renderProjectDetail(id: number) {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Projects', href: '/projects' }, { label: 'Loading...' }]);

  try {
    const data = await api.get('/projects/' + id);
    const p = data.project;
    const techStack = p.tech_stack ? JSON.parse(p.tech_stack) : [];
    const techHtml = techStack.map((t: string) => `<span class="status status-active" style="margin-right:4px">${esc(t)}</span>`).join('');
    const canConfirm = !p.delivery_confirmed && (p.status === 'completed' || p.status === 'review');
    const statusSteps = ['pending', 'in_progress', 'review', 'completed'];
    const currentStep = statusSteps.indexOf(p.status);
    let timelineHtml = '<div class="flex" style="gap:0;margin-bottom:1.5rem">';
    statusSteps.forEach((s, i) => {
      const done = i <= currentStep;
      const label = s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      timelineHtml += `<div style="flex:1;text-align:center"><div style="width:32px;height:32px;border-radius:50%;margin:0 auto 4px;display:flex;align-items:center;justify-content:center;background:${done ? 'var(--accent)' : 'var(--bg-card)'};border:2px solid ${done ? 'var(--accent)' : 'var(--border)'}"><span style="font-size:0.75rem;color:${done ? '#fff' : 'var(--text-muted)'}">${i + 1}</span></div><span style="font-size:0.75rem;color:${done ? 'var(--text-primary)' : 'var(--text-muted)'}">${label}</span></div>`;
      if (i < statusSteps.length - 1) timelineHtml += `<div style="flex:0.5;display:flex;align-items:center;padding-bottom:18px"><div style="width:100%;height:2px;background:${i < currentStep ? 'var(--accent)' : 'var(--border)'}"></div></div>`;
    });
    timelineHtml += '</div>';

    setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Projects', href: '/projects' }, { label: p.title }]);

    setHTML(content, `<div class="page-enter">
      <div class="page-header"><h1>${esc(p.title)}</h1><div class="page-header-actions">
        ${canConfirm ? '<button class="btn btn-primary" id="confirm-delivery-btn"><i data-lucide="check-circle"></i> Confirm Delivery</button>' : ''}
        ${isAdmin() ? '<button class="btn btn-secondary" id="edit-project-btn"><i data-lucide="pencil"></i> Edit</button>' : ''}
      </div></div>
      ${timelineHtml}
      <div class="card mb-2"><div class="card-body"><div class="detail-grid">
        ${isAdmin() ? `<div class="detail-item"><div class="detail-label">Client</div><div class="detail-value">${esc(p.client_name)} (${esc(p.client_email)})</div></div>` : ''}
        <div class="detail-item"><div class="detail-label">Status</div><div class="detail-value">${statusBadge(p.status)}</div></div>
        <div class="detail-item"><div class="detail-label">Priority</div><div class="detail-value">${esc((p.priority || 'medium').toUpperCase())}</div></div>
        <div class="detail-item"><div class="detail-label">Budget</div><div class="detail-value">${formatCurrency(p.budget, p.currency)}</div></div>
        <div class="detail-item"><div class="detail-label">Start Date</div><div class="detail-value">${formatDate(p.start_date)}</div></div>
        <div class="detail-item"><div class="detail-label">Deadline</div><div class="detail-value">${formatDate(p.deadline)}</div></div>
        <div class="detail-item"><div class="detail-label">Progress</div><div class="detail-value"><div class="progress-bar" style="margin-top:4px"><div class="progress-fill" style="width:${esc(p.progress)}%"></div></div><div class="progress-text">${esc(p.progress)}%</div></div></div>
        <div class="detail-item"><div class="detail-label">Delivery</div><div class="detail-value">${p.delivery_confirmed ? `<span class="text-success">Confirmed ${formatDate(p.delivery_confirmed_at)}</span>` : 'Pending'}</div></div>
        ${p.description ? `<div class="detail-item detail-full"><div class="detail-label">Description</div><div class="detail-value">${esc(p.description)}</div></div>` : ''}
        ${p.git_repo ? `<div class="detail-item"><div class="detail-label">Repository</div><div class="detail-value font-mono">${esc(p.git_repo)}</div></div>` : ''}
        ${p.live_url ? `<div class="detail-item"><div class="detail-label">Live URL</div><div class="detail-value">${esc(p.live_url)}</div></div>` : ''}
        ${techStack.length ? `<div class="detail-item detail-full"><div class="detail-label">Tech Stack</div><div class="detail-value">${techHtml}</div></div>` : ''}
      </div></div></div>
    </div>`);

    icons();
    document.getElementById('confirm-delivery-btn')?.addEventListener('click', async () => {
      if (await confirmDialog('Confirm Delivery', 'Confirm that you have received and accepted this project delivery?', 'Confirm')) {
        await api.post('/projects/' + id + '/confirm-delivery');
        toast('Delivery confirmed!', 'success');
        navigate('/projects/' + id);
      }
    });
    document.getElementById('edit-project-btn')?.addEventListener('click', async () => {
      const d = await api.get('/projects/' + id);
      openProjectForm(d.project);
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

function openProjectForm(project?: any) {
  const isEdit = !!project;
  const techStack = project?.tech_stack ? JSON.parse(project.tech_stack) : [];
  const statusOpts = ['pending', 'in_progress', 'review', 'completed', 'cancelled', 'on_hold'].map(s =>
    `<option value="${s}"${project?.status === s ? ' selected' : ''}>${s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>`).join('');
  const prioOpts = ['low', 'medium', 'high', 'urgent'].map(p =>
    `<option value="${p}"${project?.priority === p ? ' selected' : ''}>${p.charAt(0).toUpperCase() + p.slice(1)}</option>`).join('');

  openModal(isEdit ? 'Edit Project' : 'New Project',
    `<form id="project-form">
      ${!isEdit ? `<div class="form-group"><label>Client *</label><select name="client_id" required>${(window as any)._clientsOpts}</select></div>` : ''}
      <div class="form-group"><label>Title *</label><input type="text" name="title" value="${esc(project?.title || '')}" required></div>
      <div class="form-group"><label>Description</label><textarea name="description" rows="3">${esc(project?.description || '')}</textarea></div>
      <div class="form-row"><div class="form-group"><label>Status</label><select name="status">${statusOpts}</select></div>
      <div class="form-group"><label>Priority</label><select name="priority">${prioOpts}</select></div></div>
      <div class="form-row"><div class="form-group"><label>Start Date</label><input type="date" name="start_date" value="${project?.start_date?.split('T')[0] || ''}"></div>
      <div class="form-group"><label>Deadline</label><input type="date" name="deadline" value="${project?.deadline?.split('T')[0] || ''}"></div></div>
      <div class="form-row"><div class="form-group"><label>Budget</label><input type="number" step="0.01" name="budget" value="${project?.budget || ''}"></div>
      <div class="form-group"><label>Progress (%)</label><input type="number" min="0" max="100" name="progress" value="${project?.progress || 0}"></div></div>
      <div class="form-row"><div class="form-group"><label>Git Repository</label><input type="text" name="git_repo" value="${esc(project?.git_repo || '')}"></div>
      <div class="form-group"><label>Live URL</label><input type="text" name="live_url" value="${esc(project?.live_url || '')}"></div></div>
      <div class="form-group"><label>Tech Stack (comma-separated)</label><input type="text" name="tech_stack_str" value="${esc(techStack.join(', '))}"></div>
      <div class="form-group"><label>Delivery Notes</label><textarea name="delivery_notes" rows="2">${esc(project?.delivery_notes || '')}</textarea></div>
      <div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="proj-cancel">Cancel</button>
      <button type="submit" class="btn btn-primary">${isEdit ? 'Save' : 'Create Project'}</button></div></form>`, 'lg');

  document.getElementById('proj-cancel')?.addEventListener('click', closeModal);
  document.getElementById('project-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.budget = body.budget ? parseFloat(body.budget) : null;
    body.progress = parseInt(body.progress) || 0;
    body.tech_stack = body.tech_stack_str ? body.tech_stack_str.split(',').map((s: string) => s.trim()).filter(Boolean) : [];
    delete body.tech_stack_str;
    try {
      if (isEdit) { await api.put('/projects/' + project.id, body); toast('Project updated', 'success'); }
      else { await api.post('/projects', body); toast('Project created', 'success'); }
      closeModal(); navigate('/projects');
    } catch (err: any) { toast(err.message, 'error'); }
  });
}
